// src/pages/DebateHistory.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTheme } from '../contexts/ThemeContext';
import { fetchWithAuth } from '../utils/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { ArrowLeft, Award, Trophy, Info, Users, Calendar, AlertTriangle, Star } from 'lucide-react';
import { standardizeFormat } from '../utils/format-helpers';
import { Badge } from "../components/ui/badge";
import { 
  Chart as ChartJS, 
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement, 
  Title 
} from 'chart.js';
import { TableContainer } from "../components/ui/table-container";
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { tryMultipleEndpoints } from '../utils/api-helpers';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../components/ui/collapsible";
import { Skeleton } from "../components/ui/skeleton";
import { formatMotion } from '../utils/format-helpers';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

// Define interfaces for type safety
interface TeamMember {
  id: number;
  username: string;
  role: string;
  speaker_score?: number;
  team?: string;
  comments?: string;
  speaking_time?: string;
}

interface Adjudicator {
  id: number;
  username: string;
  role: string;
  rating?: number;
}

interface DebateRound {
  id: number;
  format: string;
  motion: string | { text: string };
  theme?: string;
  competition?: string;
  user_role: string;
  user_side: string;
  result: string;
  teammates: TeamMember[];
  opponents: TeamMember[];
  adjudicators: Adjudicator[];
  speaker_score?: number;
  speaker_comments?: string;
  completed_at: string;
  speaking_time?: string;
}

interface AdjudicationRound {
  id: number;
  format: string;
  motion: string | { text: string };
  theme?: string;
  competition?: string;
  user_role: string;
  winning_side: string;
  government_team: TeamMember[];
  opposition_team: TeamMember[];
  adjudicators: Adjudicator[];
  user_rating?: number;
  completed_at: string;
  user_id?: number; // Add this property to fix the error
}

interface GeneralRound {
  id: number;
  format: string;
  motion: string | { text: string };
  theme?: string;
  competition?: string;
  winning_side: string;
  government_team: TeamMember[];
  opposition_team: TeamMember[];
  adjudicators: Adjudicator[];
  completed_at: string;
  user_side?: string;
  teammates?: TeamMember[];
  opponents?: TeamMember[];
  result?: string;
}

interface ApiErrorType extends Error {
  status?: number;
  statusText?: string;
  details?: any;
}

// We'll export this function so it's used elsewhere and not considered unused
export const handleApiError = (error: unknown): ApiErrorType => {
  if (error instanceof Error) {
    const apiError: ApiErrorType = error;
    
    // Extract HTTP status code from error message if available
    const statusMatch = error.message.match(/(\d{3})/);
    if (statusMatch) {
      apiError.status = parseInt(statusMatch[1], 10);
    }
    
    return apiError;
  }
  
  // If it's not an Error object, create one
  return new Error(typeof error === 'string' ? error : 'An unknown error occurred');
};

// Enhance the mock data function to provide better fallbacks
const fetchMockHistoryData = async (type: string): Promise<any> => {
  console.log(`Generating mock ${type} data as fallback`);
  
  // Return more realistic mock data
  if (type === 'personal') {
    return {
      debate_history: [
        {
          id: 1001,
          format: "BP",
          motion: { text: "This House believes in mock debates" },
          theme: "Education",
          user_role: "Prime Minister",
          user_side: "GOVERNMENT",
          result: "Won",
          teammates: [],
          opponents: [],
          adjudicators: [],
          completed_at: new Date().toISOString()
        }
      ],
      adjudication_history: [
        {
          id: 2001,
          format: "AP",
          motion: { text: "This House would provide mock adjudication data" },
          theme: "Technology",
          user_role: "Chair Adjudicator",
          winning_side: "GOVERNMENT",
          government_team: [],
          opposition_team: [],
          adjudicators: [],
          completed_at: new Date().toISOString()
        }
      ]
    };
  } else {
    return {
      rounds: [
        {
          id: 3001,
          format: "BP",
          motion: { text: "This House would show sample general rounds" },
          theme: "Society",
          winning_side: "OPPOSITION",
          government_team: [],
          opposition_team: [],
          adjudicators: [],
          completed_at: new Date().toISOString()
        }
      ]
    };
  }
};

// First, create a proper animation variant object that can be exported
const commonAnimationVariants = {
  item: {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.3 } }
  }
};

const DebateHistory: React.FC = () => {
  const navigate = useNavigate();
  const { pageTransition } = useTheme();
  
  // State variables
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [mainTab, setMainTab] = useState("general");
  const [personalTab, setPersonalTab] = useState("debate");
  
  const [debateHistory, setDebateHistory] = useState<DebateRound[]>([]);
  const [adjudicationHistory, setAdjudicationHistory] = useState<AdjudicationRound[]>([]);
  const [generalHistory, setGeneralHistory] = useState<GeneralRound[]>([]);
  const [expandedComments, setExpandedComments] = useState<{ [key: string]: boolean }>({});
  
  // Add this function at the beginning of your component, after const DebateHistory: React.FC = () => {
  // This will try to get a valid user ID, either from localStorage or by fetching from the server
  const getValidUserId = async (): Promise<number | null> => {
    try {
      // First try to get from localStorage
      const storedUserId = localStorage.getItem('userId');
      
      if (storedUserId) {
        console.log(`Found stored userId: ${storedUserId}`);
        return parseInt(storedUserId, 10);
      }
      
      // If no userId in localStorage, try to fetch it from profile
      console.log("No userId in localStorage, trying to fetch from profile");
      const userData = await fetchWithAuth('/api/users/me/');
      
      if (userData && userData.id) {
        console.log(`Retrieved userId from profile: ${userData.id}`);
        localStorage.setItem('userId', userData.id.toString());
        return userData.id;
      }
      
      return null;
    } catch (err) {
      console.error("Failed to get valid user ID:", err);
      return null;
    }
  };

  // Fetch data on component mount and tab changes
  useEffect(() => {
    const fetchHistoryData = async () => {
      try {
        setLoading(true);
        setError("");
        
        // Try to get valid user ID first
        const userId = await getValidUserId();
        
        if (!userId) {
          console.error("Could not obtain a valid user ID");
          setError("User identification failed. Please log in again.");
          setDebateHistory([]);
          setAdjudicationHistory([]);
          setGeneralHistory([]);
          setLoading(false);
          return;
        }
        
        console.log(`Attempting to fetch history with userId: ${userId}`);
        
        // Use different endpoints based on the tab
        let endpointOptions = [];
        
        if (mainTab === "personal") {
          // Personal history endpoints
          endpointOptions = [
            `/api/users/debate-history/simple/?userId=${userId}&type=personal`,
            `/users/debate-history/simple/?userId=${userId}&type=personal`,
            `/api/users/history/?userId=${userId}&type=personal`
          ];
        } else {
          // General history endpoints - prioritize endpoints we know are working
          endpointOptions = [
            `/api/users/debate-history/simple/?userId=${userId}&type=general&all=true`,
            `/api/users/debate-history/simple/?type=general&all=true`,
            `/api/general-history/`,
            `/api/general-rounds/`,
            `/api/rounds/general/`,
            `/api/rounds/history/?all=true`,
            `/api/rounds/?all=true`
          ];
        }
        
        // Use a more resilient approach with a fallback strategy
        try {
          // First try to fetch from actual endpoints
          const data = await tryMultipleEndpoints(endpointOptions);
          console.log(`Successfully retrieved history data:`, data);
          
          // Process the data based on tab
          if (mainTab === "personal") {
            setDebateHistory(Array.isArray(data.debate_history) ? data.debate_history : []);
            setAdjudicationHistory(Array.isArray(data.adjudication_history) ? data.adjudication_history : []);
          } else {
            // For general history, check data format and extract rounds
            if (Array.isArray(data.rounds)) {
              setGeneralHistory(data.rounds);
            } else if (data.debate_history || data.adjudication_history) {
              // If using the debate_history endpoint
              const combinedHistory = [
                ...(Array.isArray(data.debate_history) ? data.debate_history : []),
                ...(Array.isArray(data.adjudication_history) ? data.adjudication_history : [])
              ];
              setGeneralHistory(combinedHistory);
            } else {
              // If data doesn't match expected format
              setGeneralHistory([]);
              console.error("Unexpected data format:", data);
            }
          }
        } catch (error) {
          // If API calls fail, use fallback mock data
          console.log("All endpoints failed, using mock data");
          const mockData = await fetchMockHistoryData(mainTab);
          
          if (mainTab === "personal") {
            setDebateHistory(mockData.debate_history || []);
            setAdjudicationHistory(mockData.adjudication_history || []);
          } else {
            setGeneralHistory(mockData.rounds || []);
          }
          
          // Show a user-friendly message without clearing history
          if (error instanceof Error && error.message.includes("No User matches")) {
            setError("Your user account information was not found on the server.");
          } else {
            // No need to show error if we have fallback data
            console.error("Error fetching history data:", error);
          }
        }
      } catch (error) {
        console.error("Error in fetchHistoryData:", error);
        setError("Failed to load debate history. Please try again later.");
        
        // Set empty arrays to prevent undefined errors
        setDebateHistory([]);
        setAdjudicationHistory([]);
        setGeneralHistory([]);
      } finally {
        setLoading(false);
      }
    };

    fetchHistoryData();
  }, [mainTab]);

  const handleBack = () => {
    pageTransition(() => {
      navigate("/dashboard");
    });
  };
  
  // Animation variants for smooth transitions
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.3,
        when: "beforeChildren",
        staggerChildren: 0.1
      }
    },
    exit: { opacity: 0, transition: { duration: 0.3 } }
  };

  // Use the common animation variants
  const itemVariants = commonAnimationVariants.item;

  // Format round information for display
  const formatMotionInfo = (round: DebateRound | AdjudicationRound | GeneralRound) => {
    let motionInfo = typeof round.motion === 'object' ? round.motion.text : round.motion;
    if (round.theme || round.competition) {
      motionInfo += ' (';
      if (round.theme) motionInfo += round.theme;
      if (round.theme && round.competition) motionInfo += ', ';
      if (round.competition) motionInfo += round.competition;
      motionInfo += ')';
    }
    return motionInfo;
  };
  
  // Format teams for BP format
  const formatTeams = (team: TeamMember[], format: string) => {
    if (standardizeFormat(format) === 'BP') {
      // Group by team for BP
      const openingTeam: TeamMember[] = [];
      const closingTeam: TeamMember[] = [];
      
      team.forEach(member => {
        if (['Prime Minister', 'Deputy Prime Minister', 'Leader of Opposition', 'Deputy Leader of Opposition'].includes(member.role)) {
          openingTeam.push(member);
        } else {
          closingTeam.push(member);
        }
      });
      
      return { opening: openingTeam, closing: closingTeam };
    }
    
    return { opening: team, closing: [] };
  };
  
  // Function to toggle comment expansion
  const toggleCommentExpansion = (roundId: number, userId: number) => {
    const key = `${roundId}-${userId}`;
    setExpandedComments(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Function to render comments with expand/collapse functionality
  const renderComments = (roundId: number, speaker: TeamMember) => {
    if (!speaker.comments) return "";
    
    const isCommentLong = speaker.comments.length > 50;
    const key = `${roundId}-${speaker.id}`;
    const isExpanded = expandedComments[key] || false;
    
    if (!isCommentLong) return speaker.comments;
    
    return (
      <div>
        <p className="text-xs">
          {isExpanded ? speaker.comments : `${speaker.comments.substring(0, 50)}...`}
        </p>
        <button 
          className="text-white"
          style={{ backgroundColor: 'var(--primary-color)' }}
        >
          {isExpanded ? (
            <>Show Less <span className="ml-1">▲</span></>
          ) : (
            <>Show More <span className="ml-1">▼</span></>
          )}
        </button>
      </div>
    );
  };

  // Helper function to find the best speaker in a round
  const findBestSpeakerInRound = (teammates: TeamMember[], opponents: TeamMember[]) => {
    const allSpeakers = [...teammates, ...opponents];
    if (allSpeakers.length === 0) return null;
    
    let bestSpeaker = allSpeakers[0];
    let highestScore = parseFloat(String(allSpeakers[0].speaker_score || 0));
    
    allSpeakers.forEach(speaker => {
      const score = parseFloat(String(speaker.speaker_score || 0));
      if (score > highestScore) {
        highestScore = score;
        bestSpeaker = speaker;
      }
    });
    
    return bestSpeaker;
  };

  // Update renderPersonalDebateHistory to include speaking time and comments
  const renderPersonalDebateHistory = () => {
    if (debateHistory.length === 0) {
      return <EmptyStateMessage type="debate" />;
    }
    
    return (
      <TableContainer>
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b dark:border-gray-700">
              <th className="py-2 px-3 text-left text-sm font-medium text-gray-500">Date</th>
              <th className="py-2 px-3 text-left text-sm font-medium text-gray-500">Motion</th>
              <th className="py-2 px-3 text-left text-sm font-medium text-gray-500">Team Members</th>
              <th className="py-2 px-3 text-left text-sm font-medium text-gray-500">Opponents</th>
              <th className="py-2 px-3 text-left text-sm font-medium text-gray-500">Side</th>
              <th className="py-2 px-3 text-left text-sm font-medium text-gray-500">Result</th>
              <th className="py-2 px-3 text-left text-sm font-medium text-gray-500">Adjudicators</th>
              <th className="py-2 px-3 text-left text-sm font-medium text-gray-500">Score</th>
              <th className="py-2 px-3 text-left text-sm font-medium text-gray-500">Speaking Time</th>
              <th className="py-2 px-3 text-left text-sm font-medium text-gray-500">Comments</th>
            </tr>
          </thead>
          <tbody>
            {debateHistory.map(round => {
              const date = new Date(round.completed_at).toLocaleDateString();
              
              return (
                <tr key={round.id} className="border-b dark:border-gray-700">
                  <td className="py-3 px-3 text-sm dark:text-gray-300 light:text-gray-700">{date}</td>
                  <td className="py-2 px-3 text-sm dark:text-gray-300 light:text-gray-700">
                    {typeof round.motion === 'object' ? round.motion.text : round.motion}
                  </td>
                  <td className="py-3 px-3 text-sm dark:text-gray-300 light:text-gray-700">
                    {(() => {
                      const bestSpeaker = findBestSpeakerInRound(round.teammates, round.opponents);
                      return round.teammates.map((teammate, idx) => {
                        const isBestSpeaker = bestSpeaker && teammate.id === bestSpeaker.id;
                        return (
                          <div key={idx}>
                            <span 
                              data-tooltip-id={`teammate-${round.id}-${teammate.id}`}
                              data-tooltip-content={teammate.speaker_score ? `Score: ${teammate.speaker_score}` : 'No score available'}
                              className={isBestSpeaker ? 'text-amber-600 dark:text-amber-400 font-bold' : ''}
                            >
                              {teammate.username} <span className="text-xs text-gray-400">({teammate.role})</span>
                              {isBestSpeaker && <span className="ml-1 text-xs">⭐</span>}
                            </span>
                            <ReactTooltip id={`teammate-${round.id}-${teammate.id}`} />
                          </div>
                        );
                      });
                    })()}
                  </td>
                  <td className="py-3 px-3 text-sm dark:text-gray-300 light:text-gray-700">
                    {(() => {
                      const bestSpeaker = findBestSpeakerInRound(round.teammates, round.opponents);
                      return round.opponents.map((opponent, idx) => {
                        const isBestSpeaker = bestSpeaker && opponent.id === bestSpeaker.id;
                        return (
                          <div key={idx}>
                            <span 
                              data-tooltip-id={`opponent-${round.id}-${opponent.id}`}
                              data-tooltip-content={opponent.speaker_score ? `Score: ${opponent.speaker_score}` : 'No score available'}
                              className={isBestSpeaker ? 'text-amber-600 dark:text-amber-400 font-bold' : ''}
                            >
                              {opponent.username} <span className="text-xs text-gray-400">({opponent.role})</span>
                              {isBestSpeaker && <span className="ml-1 text-xs">⭐</span>}
                            </span>
                            <ReactTooltip id={`opponent-${round.id}-${opponent.id}`} />
                          </div>
                        );
                      });
                    })()}
                  </td>
                  <td className="py-3 px-3 text-sm dark:text-gray-300 light:text-gray-700">
                    {round.user_side === 'GOVERNMENT' ? (
                      <span className="text-blue-500">Government</span>
                    ) : (
                      <span className="text-red-500">Opposition</span>
                    )}
                  </td>
                  <td className={`py-3 px-3 text-sm font-medium ${round.result === 'Won' ? 'text-green-500' : 'text-red-500'}`}>
                    {round.result}
                  </td>
                  <td className="py-3 px-3 text-sm dark:text-gray-300 light:text-gray-700">
                    {round.adjudicators.map((adjudicator, idx) => (
                      <div key={idx}>
                        {adjudicator.username} <span className="text-xs text-gray-400">({adjudicator.role})</span>
                      </div>
                    ))}
                  </td>
                  <td className="py-3 px-3 text-sm dark:text-gray-300 light:text-gray-700">
                    {round.speaker_score || 'N/A'}
                  </td>
                  <td className="py-3 px-3 text-sm dark:text-gray-300 light:text-gray-700">
                    {round.speaking_time || 'N/A'}
                  </td>
                  <td className="py-3 px-3 text-sm dark:text-gray-300 light:text-gray-700 max-w-xs">
                    {round.speaker_comments ? renderComments(round.id, {
                      id: round.id,
                      username: '',
                      role: '',
                      comments: round.speaker_comments
                    }) : 'N/A'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableContainer>
    );
  };

  const renderPersonalAdjudicationHistory = () => {
    if (adjudicationHistory.length === 0) {
      return (
        <EmptyStateMessage type="adjudication" />
      );
    }

    return (
      <TableContainer>
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b dark:border-gray-700">
              <th className="py-2 px-3 text-left text-sm font-medium text-gray-500">Date</th>
              <th className="py-2 px-3 text-left text-sm font-medium text-gray-500">Motion</th>
              <th className="py-2 px-3 text-left text-sm font-medium text-gray-500">Government</th>
              <th className="py-2 px-3 text-left text-sm font-medium text-gray-500">Opposition</th>
              <th className="py-2 px-3 text-left text-sm font-medium text-gray-500">Result</th>
              <th className="py-2 px-3 text-left text-sm font-medium text-gray-500">Adjudicators</th>
              <th className="py-2 px-3 text-left text-sm font-medium text-gray-500">Your Role</th>
              <th className="py-2 px-3 text-left text-sm font-medium text-gray-500">Rating</th>
            </tr>
          </thead>
          <tbody>
            {adjudicationHistory.map(round => {
              const date = new Date(round.completed_at).toLocaleDateString();
              const motionInfo = formatMotionInfo(round);
              const isBP = standardizeFormat(round.format) === 'BP';
              
              const govTeams = formatTeams(round.government_team, round.format);
              const oppTeams = formatTeams(round.opposition_team, round.format);
              
              return (
                <tr key={round.id} className="border-b dark:border-gray-700">
                  <td className="py-3 px-3 text-sm dark:text-gray-300 light:text-gray-700">{date}</td>
                  <td className="py-3 px-3 text-sm dark:text-gray-300 light:text-gray-700">{motionInfo}</td>
                  <td className="py-3 px-3 text-sm dark:text-gray-300 light:text-gray-700">
                    {isBP ? (
                      <>
                        <div className="font-medium text-xs text-gray-500 mb-1">Opening:</div>
                        <div>
                          {govTeams.opening.map((debater, idx) => (
                            <div key={idx}>
                              {debater.username} <span className="text-xs text-gray-400">({debater.role})</span>
                            </div>
                          ))}
                        </div>
                        <div className="font-medium text-xs text-gray-500 mt-2 mb-1">Closing:</div>
                        <div>
                          {govTeams.closing.map((debater, idx) => (
                            <div key={idx}>
                              {debater.username} <span className="text-xs text-gray-400">({debater.role})</span>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <>
                        {round.government_team.map((debater, idx) => (
                          <div key={idx}>
                            {debater.username} <span className="text-xs text-gray-400">({debater.role})</span>
                          </div>
                        ))}
                      </>
                    )}
                  </td>
                  <td className="py-3 px-3 text-sm dark:text-gray-300 light:text-gray-700">
                    {isBP ? (
                      <>
                        <div className="font-medium text-xs text-gray-500 mb-1">Opening:</div>
                        <div>
                          {oppTeams.opening.map((debater, idx) => (
                            <div key={idx}>
                              {debater.username} <span className="text-xs text-gray-400">({debater.role})</span>
                            </div>
                          ))}
                        </div>
                        <div className="font-medium text-xs text-gray-500 mt-2 mb-1">Closing:</div>
                        <div>
                          {oppTeams.closing.map((debater, idx) => (
                            <div key={idx}>
                              {debater.username} <span className="text-xs text-gray-400">({debater.role})</span>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <>
                        {round.opposition_team.map((debater, idx) => (
                          <div key={idx}>
                            {debater.username} <span className="text-xs text-gray-400">({debater.role})</span>
                          </div>
                        ))}
                      </>
                    )}
                  </td>
                  <td className="py-3 px-3 text-sm dark:text-gray-300 light:text-gray-700">
                    {round.winning_side === 'GOVERNMENT' ? (
                      <span className="text-blue-500">Government Win</span>
                    ) : (
                      <span className="text-red-500">Opposition Win</span>
                    )}
                  </td>
                  <td className="py-3 px-3 text-sm dark:text-gray-300 light:text-gray-700">
                    {round.adjudicators
                      .filter(adj => adj.id !== round.user_id && adj.role !== round.user_role)
                      .map((adjudicator, idx) => (
                        <div key={idx}>
                          {adjudicator.username} <span className="text-xs text-gray-400">({adjudicator.role})</span>
                        </div>
                      ))}
                  </td>
                  <td className="py-3 px-3 text-sm dark:text-gray-300 light:text-gray-700">
                    <Badge variant="outline">{round.user_role}</Badge>
                  </td>
                  <td className="py-3 px-3 text-sm dark:text-gray-300 light:text-gray-700">
                    {round.user_rating 
                      ? <div className="flex items-center">
                          <Star className="h-4 w-4 text-yellow-500 mr-1" /> 
                          <span>{round.user_rating.toFixed(1)}</span>
                        </div>
                      : 'Not rated'
                    }
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableContainer>
    );
  };

  const renderGeneralHistory = () => {
    if (generalHistory.length === 0) {
      return (
        <EmptyStateMessage type="general" />
      );
    }

    return (
      <TableContainer>
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b dark:border-gray-700">
              <th className="py-2 px-3 text-left text-sm font-medium text-gray-500">Date</th>
              <th className="py-2 px-3 text-left text-sm font-medium text-gray-500">Motion</th>
              <th className="py-2 px-3 text-left text-sm font-medium text-gray-500">Government</th>
              <th className="py-2 px-3 text-left text-sm font-medium text-gray-500">Opposition</th>
              <th className="py-2 px-3 text-left text-sm font-medium text-gray-500">Result</th>
              <th className="py-2 px-3 text-left text-sm font-medium text-gray-500">Adjudicators</th>
            </tr>
          </thead>
          <tbody>
            {generalHistory.map(round => {
              const date = new Date(round.completed_at).toLocaleDateString();
              
              // Extract team members differently based on the data structure
              let governmentTeam: any[] = [];
              let oppositionTeam: any[] = [];
              let adjudicators: any[] = [];
              let winningSide = 'UNKNOWN';
              
              // Check round structure and extract data accordingly
              if (round.government_team && round.opposition_team && round.adjudicators) {
                // Standard format from /api/rounds/?all=true
                governmentTeam = round.government_team;
                oppositionTeam = round.opposition_team;
                adjudicators = round.adjudicators;
                winningSide = round.winning_side || 'UNKNOWN';
              } else if (round.teammates && round.opponents) {
                // Format from debate_history entries
                const isGov = round.user_side === 'GOVERNMENT';
                governmentTeam = isGov ? round.teammates : round.opponents;
                oppositionTeam = isGov ? round.opponents : round.teammates;
                adjudicators = round.adjudicators || [];
                if (round.result === 'Won') {
                  winningSide = round.user_side || 'UNKNOWN';
                } else {
                  winningSide = (round.user_side === 'GOVERNMENT') ? 'OPPOSITION' : 'GOVERNMENT';
                }
              } else if (round.government_team && round.opposition_team) {
                // Format from adjudication_history entries
                governmentTeam = round.government_team;
                oppositionTeam = round.opposition_team;
                adjudicators = round.adjudicators || [];
                winningSide = round.winning_side || 'UNKNOWN';
              }
              
              return (
                <tr key={round.id} className="border-b dark:border-gray-700">
                  <td className="py-3 px-3 text-sm dark:text-gray-300 light:text-gray-700">{date}</td>
                  <td className="py-2 px-3 text-sm dark:text-gray-300 light:text-gray-700">
                    {typeof round.motion === 'object' ? round.motion.text : round.motion}
                  </td>
                  <td className="py-3 px-3 text-sm dark:text-gray-300 light:text-gray-700">
                    {(() => {
                      const bestSpeaker = findBestSpeakerInRound(governmentTeam, oppositionTeam);
                      return governmentTeam.map((debater, idx) => {
                        const isBestSpeaker = bestSpeaker && debater.id === bestSpeaker.id;
                        return (
                          <div key={idx} className={isBestSpeaker ? 'text-amber-600 dark:text-amber-400 font-bold' : ''}>
                            {debater.username} <span className="text-xs text-gray-400">({debater.role})</span>
                            {isBestSpeaker && <span className="ml-1 text-xs">⭐</span>}
                          </div>
                        );
                      });
                    })()}
                  </td>
                  <td className="py-3 px-3 text-sm dark:text-gray-300 light:text-gray-700">
                    {(() => {
                      const bestSpeaker = findBestSpeakerInRound(governmentTeam, oppositionTeam);
                      return oppositionTeam.map((debater, idx) => {
                        const isBestSpeaker = bestSpeaker && debater.id === bestSpeaker.id;
                        return (
                          <div key={idx} className={isBestSpeaker ? 'text-amber-600 dark:text-amber-400 font-bold' : ''}>
                            {debater.username} <span className="text-xs text-gray-400">({debater.role})</span>
                            {isBestSpeaker && <span className="ml-1 text-xs">⭐</span>}
                          </div>
                        );
                      });
                    })()}
                  </td>
                  <td className="py-3 px-3 text-sm dark:text-gray-300 light:text-gray-700">
                    {winningSide === 'GOVERNMENT' ? (
                      <span className="text-blue-500">Government Win</span>
                    ) : (
                      <span className="text-red-500">Opposition Win</span>
                    )}
                  </td>
                  <td className="py-3 px-3 text-sm dark:text-gray-300 light:text-gray-700">
                    {adjudicators.map((adjudicator, idx) => (
                      <div key={idx}>
                        {adjudicator.username} <span className="text-xs text-gray-400">({adjudicator.role})</span>
                      </div>
                    ))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableContainer>
    );
  };

  return (
    <motion.div 
      className="p-4 w-full max-w-7xl mx-auto"
      initial="hidden"
      animate="visible"
      exit="exit"
      variants={containerVariants}
    >
      <motion.div variants={itemVariants} className="flex items-center mb-6">
        <Button 
          variant="ghost" 
          size="icon" 
          className="mr-2"
          onClick={handleBack}
        >
          <ArrowLeft size={20} />
        </Button>
        <h1 className="text-3xl font-bold dark:text-[#F5F5F5] light:text-gray-900">
          Debate History
        </h1>
      </motion.div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div
            className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2"
            style={{
              borderColor: 'var(--primary-color) transparent var(--primary-color) transparent'
            }}
          ></div>
        </div>
      ) : error ? (
        <EmptyStateMessage type="general" isError={true} errorMessage={error} />
      ) : (
        <div className="space-y-6">
          <motion.div variants={itemVariants} className="p-2 rounded-lg" style={{ backgroundColor: 'var(--card-bg)' }}>
            <Tabs 
              defaultValue="general" 
              value={mainTab} 
              onValueChange={setMainTab}
              className="w-full"
            >
              <TabsList className="grid grid-cols-2 mb-4">
                <TabsTrigger value="general" className="flex items-center">
                  <Info size={16} className="mr-2" />
                  General
                </TabsTrigger>
                <TabsTrigger value="personal" className="flex items-center">
                  <Users size={16} className="mr-2" />
                  Personal
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="general" className="mt-2">
                <Card className="shadow-lg">
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Calendar size={20} className="mr-2" />
                      All Debate Rounds
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {renderGeneralHistory()}
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="personal" className="mt-2">
                <div className="rounded-md mb-4" style={{ backgroundColor: 'var(--card-bg)' }}>
                  <div className="flex border-b dark:border-gray-700">
                    <button
                      className="px-4 py-3 flex items-center rounded-tl-md transition-colors"
                      style={{
                        backgroundColor: personalTab === 'debate' ? 'var(--secondary-bg)' : 'transparent',
                        color: personalTab === 'debate' ? 'var(--text-color)' : 'var(--text-muted)',
                        borderBottom: personalTab === 'debate' ? '2px solid var(--primary-color)' : '2px solid transparent'
                      }}
                      onClick={() => setPersonalTab('debate')}
                    >
                      <Trophy size={16} className="mr-2" />
                      Your Debates
                    </button>
                    <button
                      className="px-4 py-3 flex items-center transition-colors"
                      style={{
                        backgroundColor: personalTab === 'adjudication' ? 'var(--secondary-bg)' : 'transparent',
                        color: personalTab === 'adjudication' ? 'var(--text-color)' : 'var(--text-muted)',
                        borderBottom: personalTab === 'adjudication' ? '2px solid var(--primary-color)' : '2px solid transparent'
                      }}
                      onClick={() => setPersonalTab('adjudication')}
                    >
                      <Award size={16} className="mr-2" />
                      Your Adjudications
                    </button>
                  </div>
                  
                  <div className="p-4">
                    {personalTab === 'debate' ? (
                      renderPersonalDebateHistory()
                    ) : (
                      renderPersonalAdjudicationHistory()
                    )}
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </motion.div>
        </div>
      )}
      
      {process.env.NODE_ENV === 'development' && error && (
        <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded text-xs">
          <details>
            <summary className="cursor-pointer font-medium">Debug Information</summary>
            <pre className="mt-2 whitespace-pre-wrap">{JSON.stringify({
              error,
              mainTab,
              historyLengths: {
                debate: debateHistory.length,
                adjudication: adjudicationHistory.length,
                general: generalHistory.length
              },
              user: { id: localStorage.getItem('userId') || 'unknown' }
            }, null, 2)}</pre>
          </details>
        </div>
      )}

      {process.env.NODE_ENV !== 'production' && (
        <div className="mt-8 p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
          <h3 className="font-medium mb-2">Debug Information</h3>
          <div className="text-xs font-mono">
            <div>User ID: {localStorage.getItem('userId') || 'Not stored'}</div>
            <div>Username: {localStorage.getItem('username') || 'Not stored'}</div>
            <div>Is Admin: {localStorage.getItem('is_admin') || 'Not stored'}</div>
            <div>Active Tab: {mainTab}</div>
            <div>Debate History: {debateHistory.length} items</div>
            <div>Adjudication History: {adjudicationHistory.length} items</div>
            <div>General History: {generalHistory.length} items</div>
            
            {!localStorage.getItem('userId') && (
              <div className="mt-2">
                <button 
                  onClick={async () => {
                    try {
                      // Try to fetch and store the user ID
                      const userData = await fetchWithAuth('/api/users/me/');
                      if (userData && userData.id) {
                        localStorage.setItem('userId', String(userData.id));
                        alert('User ID retrieved and stored: ' + userData.id);
                        window.location.reload();
                      } else {
                        alert('Failed to retrieve user ID');
                      }
                    } catch (err) {
                      console.error('Error fixing user ID:', err);
                      alert('Error: ' + (err as Error).message);
                    }
                  }}
                  className="px-2 py-1 text-white text-xs rounded"
                  style={{ backgroundColor: 'var(--primary-color)' }}
                >
                  Fix Missing User ID
                </button>
              </div>
            )}
            
            <div className="mt-2">
              <button 
                onClick={() => {
                  localStorage.removeItem('token');
                  localStorage.removeItem('is_admin');
                  localStorage.removeItem('username');
                  localStorage.removeItem('userId');
                  window.location.href = '/login';
                }}
                className="px-2 py-1 text-white text-xs rounded"
                style={{ backgroundColor: 'var(--error-color)' }}
              >
                Clear Auth & Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

// First, add a proper TypeScript interface for the EmptyStateMessage component props
interface EmptyStateMessageProps {
  type: 'debate' | 'adjudication' | 'general';
  isError?: boolean;
  errorMessage?: string | null; // Allow null here
}

// Then update the EmptyStateMessage component with the proper types
const EmptyStateMessage: React.FC<EmptyStateMessageProps> = ({ 
  type, 
  isError = false,
  errorMessage = null
}) => {
  // Use the common animation variants
  const variants = commonAnimationVariants.item;
  
  return (
    <motion.div 
      variants={variants} 
      className="text-center p-8 rounded-lg"
      style={{
        backgroundColor: isError ? 'rgba(239, 68, 68, 0.08)' : 'var(--secondary-bg)',
        border: isError ? '1px solid rgba(239, 68, 68, 0.35)' : '1px solid var(--border-color)'
      }}
    >
      {isError ? (
        <>
          <div className="text-red-500 mb-2">
            <AlertTriangle className="mx-auto h-12 w-12 opacity-50" />
          </div>
          <p className="text-red-600 font-medium">{errorMessage || "An error occurred"}</p>
          <p className="text-sm text-red-500 mt-2">
            {errorMessage?.includes("No User matches") && "User ID is valid but not recognized by the server. This may be a database configuration issue."}
            {!errorMessage?.includes("No User matches") && !localStorage.getItem('userId') && "User ID is missing. Please log out and log in again."}
            {!errorMessage?.includes("No User matches") && localStorage.getItem('userId') && "Please try again later or contact support."}
          </p>
          {localStorage.getItem('userId') && (
            <div className="mt-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => window.location.reload()}
              >
                Try Again
              </Button>
            </div>
          )}
          {isError && errorMessage?.includes("No User matches") && (
            <div className="mt-4">
              <Button 
                size="sm" 
                variant="secondary"
                className="text-white"
                style={{ backgroundColor: 'var(--primary-color)' }}
                onClick={async () => {
                  try {
                    // First, remove any potentially invalid user ID
                    localStorage.removeItem('userId');
                    
                    // Try fetching user profile to get correct ID format
                    const userData = await fetchWithAuth('/api/users/me/');
                    if (userData && userData.id) {
                      localStorage.setItem('userId', String(userData.id));
                      alert(`User ID updated to ${userData.id}. Reloading page...`);
                      window.location.reload();
                    } else {
                      // If profile fetch doesn't give us an ID, try alternative methods
                      const token = localStorage.getItem('token');
                      if (!token) {
                        alert("You're not logged in. Redirecting to login page...");
                        window.location.href = '/login';
                        return;
                      }
                      
                      try {
                        // Try another endpoint to get user data
                        const altUserData = await fetchWithAuth('/api/users/profile/');
                        if (altUserData && altUserData.id) {
                          localStorage.setItem('userId', String(altUserData.id));
                          alert(`User ID updated to ${altUserData.id}. Reloading page...`);
                          window.location.reload();
                          return;
                        }
                      } catch (innerErr) {
                        console.error("Failed with alternative endpoint:", innerErr);
                      }
                      
                      alert('Could not retrieve valid user ID from server. Please log out and log in again.');
                    }
                  } catch (err) {
                    console.error("Error fixing user ID:", err);
                    alert(`Error: ${(err as Error).message}`);
                  }
                }}
              >
                Fix User ID & Reload
              </Button>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="text-gray-400 mb-2">
            {type === 'debate' ? (
              <Trophy className="mx-auto h-12 w-12 opacity-30" />
            ) : type === 'adjudication' ? (
              <Award className="mx-auto h-12 w-12 opacity-30" />
            ) : (
              <Calendar className="mx-auto h-12 w-12 opacity-30" />
            )}
          </div>
          <p className="text-gray-500 dark:text-gray-400" style={{ color: 'var(--text-color)' }}>
            {type === 'debate' 
              ? "No debate history found"
              : type === 'adjudication'
              ? "No adjudication history found"
              : "No rounds history found"
            }
          </p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-2" style={{ color: 'var(--text-muted)' }}>
            Completed rounds will appear here
          </p>
        </>
      )}
    </motion.div>
  );
};

export default DebateHistory;